import { Link } from 'react-router-dom';
import { Check, ChevronLeft, CircleAlert } from 'lucide-react';
import { Button, Skeleton } from '@design-system';
import { useReplyEditor, type ReplyEditorStatus } from '../hooks/use-reply-editor';
import { EmailComposer } from './EmailComposer';
import { EmailPreview } from './EmailPreview';
import { ReplyChance } from './ReplyChance';
import { MoreSettings } from './MoreSettings';
import { NextSteps } from './NextSteps';
import { StatusPill } from './StatusPill';

function StatusFor({ status, version }: { status: ReplyEditorStatus; version: number | null }) {
  if (status === 'live') return <StatusPill tone="live">Live · version {version}</StatusPill>;
  if (status === 'changes') return <StatusPill tone="changes">Changes not published</StatusPill>;
  if (status === 'off') return <StatusPill tone="off">Off</StatusPill>;
  if (status === 'unavailable')
    return <StatusPill tone="unavailable">Not available yet</StatusPill>;
  return <StatusPill tone="draft">Draft</StatusPill>;
}

/** One reply: the words with their information, the next steps, and the email as it will look. */
export function ReplyEditor({ entryId }: { entryId: string }) {
  const editor = useReplyEditor(entryId);

  if (editor.error)
    return (
      <p className="text-sm text-red-700">Could not open this reply: {editor.error.message}</p>
    );
  if (editor.loading || !editor.reply || !editor.catalog)
    return <Skeleton className="h-[640px] w-full" />;
  const { reply, catalog } = editor;

  return (
    <div className="mx-auto flex max-w-[1320px] flex-col gap-4">
      <Link
        to="/wizard/magic-email?tab=library"
        className="inline-flex min-h-11 items-center gap-1.5 self-start text-sm font-medium text-zinc-600"
      >
        <ChevronLeft
          className="size-4"
          aria-hidden
        />{' '}
        All replies
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-1.5">
          <span className="text-[13px] font-semibold uppercase tracking-[0.04em] text-zinc-600">
            {reply.category}
          </span>
          <h1 className="text-[26px] font-bold tracking-[-0.01em]">
            {reply.title || 'Untitled reply'}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <StatusFor
            status={editor.status}
            version={editor.publishedVersion}
          />
          {editor.canTurnOff && (
            <Button
              variant="ghost"
              className="h-11"
              onClick={editor.turnOff}
            >
              Turn off
            </Button>
          )}
          <Button
            variant="outline"
            className="h-11 px-[18px] font-semibold"
            disabled={!editor.dirty || editor.saving}
            onClick={editor.save}
          >
            {editor.saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            className="h-11 px-[18px] font-semibold"
            disabled={!editor.canPublish || editor.publishing}
            onClick={editor.publish}
          >
            {editor.publishing ? 'Publishing…' : editor.publishLabel}
          </Button>
        </div>
      </div>

      {editor.justPublished !== null && (
        <div
          role="status"
          className="flex items-center gap-2.5 rounded-[10px] bg-[#E7F6EC] px-4 py-3 text-sm font-medium text-[#1B6B3A]"
        >
          <Check
            className="size-[18px]"
            aria-hidden
          />
          Published. The next email this reply answers will use version {editor.justPublished}.
        </div>
      )}

      {editor.unavailable.length > 0 && (
        <div className="flex items-start gap-2.5 rounded-[10px] border border-[#F0D9AE] bg-[#FFF8EC] px-4 py-3 text-sm text-[#8A4B00]">
          <CircleAlert
            className="mt-0.5 size-4 flex-none"
            aria-hidden
          />
          <div className="flex flex-col gap-1">
            <span className="font-semibold">
              Not available yet. You can still edit and save it.
            </span>
            {editor.unavailable.map(problem => (
              <span key={problem.message}>{problem.message}</span>
            ))}
          </div>
        </div>
      )}

      {editor.documentError && (
        <p className="rounded-[10px] bg-red-50 px-4 py-3 text-sm text-red-800">
          These words could not be opened in the editor ({editor.documentError}). Saving will
          replace them with what is shown below.
        </p>
      )}

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_600px] flex-col gap-4">
          {editor.toFix.length > 0 && (
            <section className="flex flex-col gap-1.5 rounded-xl border border-zinc-200 bg-white px-5 py-4">
              <h2 className="text-base font-semibold">Before you can publish</h2>
              <ul className="ml-5 list-disc text-sm text-zinc-700">
                {editor.toFix.map(problem => (
                  <li key={problem.message}>{problem.message}</li>
                ))}
              </ul>
            </section>
          )}
          <EmailComposer
            resetKey={editor.editorKey}
            catalog={catalog}
            document={reply.document ?? []}
            onChange={editor.setDocument}
          />
          <NextSteps
            actions={reply.actions}
            steps={catalog.steps}
            onChange={editor.setStep}
          />
          <MoreSettings
            reply={reply}
            owners={catalog.owners}
            versions={editor.versions}
            publishedVersion={editor.publishedVersion}
            change={editor.change}
            loadVersion={editor.loadVersion}
          />
        </div>
        <div className="flex min-w-0 flex-[1_1_440px] flex-col gap-4">
          <EmailPreview
            query={editor.preview.query}
            setFlag={editor.preview.setFlag}
            highlight={editor.preview.highlight}
            setHighlight={editor.preview.setHighlight}
          />
          <ReplyChance
            entryId={entryId}
            reply={reply}
          />
        </div>
      </div>
    </div>
  );
}
